import React from 'react'
import { reduxForm, Field, InjectedFormProps } from 'redux-form'
import { connect } from 'react-redux'

import { InputField, CheckboxField, SelectField, TimezoneSelect, UserSelect } from '../../components/inputs'
import LoaderButton from '../../components/loaderButton'
import S3FileUploader from '../../components/S3FileUploader'
import SignatureUploader from '../../components/SignatureUploader'
import { fetchPresignedS3UrlForGet } from '../../actions/configurationActions'

// Define a more specific type for the form values if possible
interface ConfigurationFormValues {
  [key: string]: any // Using any for now for simplicity
}

// Props passed by the parent component
interface ConfigurationFormOwnProps {
  onSubmit: (values: ConfigurationFormValues) => void
  timezones?: string[]
  users?: { id: string; firstName: string; lastName: string; email: string }[]
  isLoadingUsers?: boolean
}

// Combine injected redux-form props with own props
type ConfigurationFormProps = InjectedFormProps<
  ConfigurationFormValues,
  ConfigurationFormOwnProps
> &
  ConfigurationFormOwnProps

const formFields = [
  {
    name: 'androidCprEnabled',
    label: 'Android Cpr Enabled',
    component: CheckboxField,
    type: 'checkbox',
  },
  {
    name: 'androidUpgradeReason',
    label: 'Android Upgrade Reason',
    component: InputField,
  },
  { name: 'appStoreId', label: 'App Store Id', component: InputField },
  { name: 'appStoreUrl', label: 'App Store Url', component: InputField },
  {
    name: 'askingPricePercentOver',
    label: 'Asking Price Percent Over',
    component: InputField,
  },
  {
    name: 'askingPricePercentUnder',
    label: 'Asking Price Percent Under',
    component: InputField,
  },
  {
    name: 'askingPriceStep',
    label: 'Asking Price Step',
    component: InputField,
  },
  { name: 'callCenterLink', label: 'Call Center Link', component: InputField },
  {
    name: 'callCenterLogoPath',
    label: 'Call Center Logo',
    component: S3FileUploader,
  },
  { name: 'callCenterName', label: 'Call Center Name', component: InputField },
  { name: 'defaultOwnerId', label: 'Default Owner ID', component: UserSelect },
  {
    name: 'ensuretyAccountCode',
    label: 'Payment Services: Ensurety Account Code',
    component: InputField,
  },
  {
    name: 'downPaymentPercentDefault',
    label: 'Down Payment Percent Default',
    component: InputField,
  },
  {
    name: 'downPaymentPercentMin',
    label: 'Down Payment Percent Min',
    component: InputField,
  },
  {
    name: 'downPaymentStep',
    label: 'Down Payment Step',
    component: InputField,
  },
  {
    name: 'estimatedPaymentDownPct',
    label: 'Estimated Payment Down Pct',
    component: InputField,
  },
  {
    name: 'estimatedPaymentTerm',
    label: 'Estimated Payment Term',
    component: InputField,
  },
  {
    name: 'estimatedPaymentTier',
    label: 'Estimated Payment Tier',
    component: InputField,
  },
  { name: 'faqUrl', label: 'Faq Url', component: InputField },
  { name: 'feedbackEmail', label: 'Feedback Email', component: InputField },
  { name: 'infoEmail', label: 'Info Email', component: InputField },
  {
    name: 'iosUpgradeReason',
    label: 'Ios Upgrade Reason',
    component: InputField,
  },
  {
    name: 'maxMinAmountFinanced',
    label: 'Max Min Amount Financed',
    component: InputField,
  },
  {
    name: 'maxMonthlyPayment',
    label: 'Max Monthly Payment',
    component: InputField,
  },
  { name: 'maxTerm', label: 'Max Term', component: InputField },
  { name: 'maxVehicleAge', label: 'Max Vehicle Age', component: InputField },
  {
    name: 'maxVehicleMileage',
    label: 'Max Vehicle Mileage',
    component: InputField,
  },
  { name: 'minAndroidApp', label: 'Min Android App', component: InputField },
  { name: 'minIosApp', label: 'Min Ios App', component: InputField },
  { name: 'minIosOs', label: 'Min Ios Os', component: InputField },
  {
    name: 'minMonthlyPayment',
    label: 'Min Monthly Payment',
    component: InputField,
  },
  { name: 'minTerm', label: 'Min Term', component: InputField },
  {
    name: 'monthlyPaymentStep',
    label: 'Monthly Payment Step',
    component: InputField,
  },
  {
    name: 'offerAmountAdditionalPercentOver',
    label: 'Offer Amount Additional Percent Over',
    component: InputField,
  },
  {
    name: 'offerAmountAdditionalPercentUnder',
    label: 'Offer Amount Additional Percent Under',
    component: InputField,
  },
  {
    name: 'offerAmountStep',
    label: 'Offer Amount Step',
    component: InputField,
  },
  { name: 'periodsPerYear', label: 'Periods Per Year', component: InputField },
  {
    name: 'profitDiscountMax',
    label: 'Profit Discount Max',
    component: InputField,
  },
  {
    name: 'validationDiscountMax',
    label: 'Validation Discount Max',
    component: InputField,
  },
  {
    name: 'profitDiscountPercent',
    label: 'Profit Discount Percent',
    component: InputField,
  },
  {
    name: 'profitMarkupDefault',
    label: 'Profit Markup Default',
    component: InputField,
  },
  {
    name: 'refiCashBackStep',
    label: 'Refi Cash Back Step',
    component: InputField,
  },
  {
    name: 'refiMinCashBack',
    label: 'Refi Min Cash Back',
    component: InputField,
  },
  { name: 'refiMinPmtDiff', label: 'Refi Min Pmt Diff', component: InputField },
  { name: 'supportEmail', label: 'Support Email', component: InputField },
  {
    name: 'signature',
    label: 'Signature',
    component: SignatureUploader,
  },
  {
    name: 'supportHoursText',
    label: 'Support Hours Text',
    component: InputField,
  },
  { name: 'supportPhone', label: 'Support Phone', component: InputField },
  {
    name: 'testMode',
    label: 'Test Mode enabled?',
    component: CheckboxField,
    type: 'checkbox',
  },
  {
    name: 'homeProtectionHandoff',
    label: 'Home Protection enabled?',
    component: CheckboxField,
    type: 'checkbox',
  },
  {
    name: 'homeProtectionHandoffApiKey',
    label: 'Home Protection API Key',
    component: InputField,
    type: 'password',
    placeholder: 'Enter a new key to configure or replace it',
  },
  {
    name: 'clearHomeProtectionHandoffApiKey',
    label: 'Clear existing Home Protection API Key',
    component: CheckboxField,
    type: 'checkbox',
  },
  {
    name: 'blinker2OrgCode',
    label: 'Blinker 2.0 Org Code',
    component: InputField,
  },
  {
    name: 'firstPaymentMinDays',
    label: 'First Payment Min Days',
    component: InputField,
  },
  {
    name: 'firstPaymentMaxDays',
    label: 'First Payment Max Days',
    component: InputField
  }
]

const ConfigurationFormComponent: React.FC<ConfigurationFormProps> = (
  props
) => {
  const { handleSubmit, submitting, initialValues } = props
  const timezones = props.timezones ?? initialValues?.availableTimezones ?? []
  const [logoUrl, setLogoUrl] = React.useState<string | null>(
    initialValues?.callCenterLogoPath ?? null
  )

  const handleFormSubmit = (values: ConfigurationFormValues) => {
    return props.onSubmit(values)
  }

  return (
    <form
      className="form"
      name="ConfigurationForm"
      onSubmit={handleSubmit(handleFormSubmit)}
    >
      {formFields.map((field) => {
        if (
          field.name === 'clearHomeProtectionHandoffApiKey' &&
          !initialValues?.homeProtectionHandoffApiKeyConfigured
        ) {
          return null
        }

        if (field.component === S3FileUploader) {
          return (
            <div className="form__group" key={field.name}>
              <label className="form__label">{field.label}</label>
              <S3FileUploader
                existingKey={logoUrl}
                onUploadComplete={(key) => {
                  props.change('callCenterLogoPath', key)
                  setLogoUrl(key)
                }}
                onRemove={() => {
                  props.change('callCenterLogoPath', null)
                  setLogoUrl(null)
                }}
              />
              <Field
                type="hidden"
                name="callCenterLogoPath"
                component={InputField}
              />
            </div>
          )
        }
        if (field.component === SignatureUploader) {
          return (
            <div className="form__group" key={field.name}>
              <label className="form__label">{field.label}</label>
              <SignatureUploader configuration={initialValues} />
            </div>
          )
        }
        if (field.component === UserSelect) {
          return (
            <UserSelect
              key={field.name}
              label="Default Owner ID"
              name="defaultOwnerId"
              defaultOption="Select a user"
              users={props.users ?? initialValues?.users ?? []}
              isLoading={props.isLoadingUsers ?? false}
            />
          )
        }
        return (
          <React.Fragment key={field.name}>
            <Field
              type={field.type || 'text'}
              label={field.label}
              name={field.name}
              component={field.component}
              placeholder={field.placeholder}
            />
            {field.name === 'homeProtectionHandoffApiKey' && (
              <p className="form__help-text">
                {initialValues?.homeProtectionHandoffApiKeyConfigured
                  ? 'A key is configured. Leave this blank to keep it, or enter a new key to replace it.'
                  : 'This key is write-only and will not be shown after saving.'}
              </p>
            )}
          </React.Fragment>
        )
      })}

      <Field
        label="Payment Services: Down Payment Flow"
        name="paymentFlow"
        component={SelectField}
      >
        <option value="ensurety_efs">Traditional EFS payment flow</option>
        <option value="direct_fluidpay">Direct FluidPay payment flow</option>
      </Field>

      <TimezoneSelect
        label="Timezone"
        name="timezone"
        defaultOption="Select a timezone"
        timezones={timezones}
      />

      <hr className="form__hr" />
      <div className="form__group">
        <LoaderButton
          type="submit"
          className="button button--primary"
          isLoading={submitting}
        >
          Save
        </LoaderButton>
      </div>
    </form>
  )
}

const ConnectedConfigurationForm = connect(
  null,
  null
)(ConfigurationFormComponent)

export default reduxForm<ConfigurationFormValues, ConfigurationFormOwnProps>({
  form: 'configurationForm',
  enableReinitialize: true,
  destroyOnUnmount: true,
  validate: (values) => {
    const errors: { [key: string]: string } = {}

    const requiredFields = [
      'androidUpgradeReason',
      'appStoreId',
      'appStoreUrl',
      'askingPricePercentOver',
      'askingPricePercentUnder',
      'askingPriceStep',
      'callCenterName',
      'callCenterLink',
      'ensuretyAccountCode',
      'downPaymentPercentDefault',
      'downPaymentPercentMin',
      'downPaymentStep',
      'estimatedPaymentDownPct',
      'estimatedPaymentTerm',
      'estimatedPaymentTier',
      'faqUrl',
      'feedbackEmail',
      'infoEmail',
      'iosUpgradeReason',
      'maxMinAmountFinanced',
      'maxMonthlyPayment',
      'maxTerm',
      'maxVehicleAge',
      'maxVehicleMileage',
      'minAndroidApp',
      'minIosApp',
      'minIosOs',
      'minMonthlyPayment',
      'minTerm',
      'monthlyPaymentStep',
      'offerAmountAdditionalPercentOver',
      'offerAmountAdditionalPercentUnder',
      'offerAmountStep',
      'periodsPerYear',
      'profitDiscountMax',
      'profitDiscountPercent',
      'profitMarkupDefault',
      'refiCashBackStep',
      'refiMinCashBack',
      'refiMinPmtDiff',
      'supportEmail',
      'supportHoursText',
      'supportPhone',
      // 'callCenterLogoPath', // Removed from required fields as it's handled by Uppy
    ]

    requiredFields.forEach((field) => {
      // 0 is a valid value (e.g. estimatedPaymentDownPct), so check for absence
      // rather than falsiness -- a falsy check silently blocks the whole submit.
      const value = values[field]
      if (value === undefined || value === null || value === '') {
        errors[field] = 'Required'
      }
    })

    const numericFields = [
      'askingPricePercentOver',
      'askingPricePercentUnder',
      'downPaymentPercentDefault',
      'downPaymentPercentMin',
      'estimatedPaymentDownPct',
      'estimatedPaymentTerm',
      'estimatedPaymentTier',
      'maxTerm',
      'maxVehicleAge',
      'maxVehicleMileage',
      'minTerm',
      'offerAmountAdditionalPercentOver',
      'offerAmountAdditionalPercentUnder',
      'periodsPerYear',
      'profitDiscountPercent',
    ]

    numericFields.forEach((field) => {
      if (values[field] && isNaN(Number(values[field]))) {
        errors[field] = 'Must be a number'
      }
    })

    return errors
  },
})(ConnectedConfigurationForm)
